// frontend/src/components/OrganizationSettings.js - Themed version with professional design
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../axiosConfig';
import { useChangeTracker } from '../utils/changeTracker';
import referenceService from '../services/referenceService';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';
import { useScrollableTabs } from '../hooks/useScrollableTabs';
import {
  Building,
  MapPin,
  Clock,
  Calendar,
  Upload,
  Save,
  Image,
  Navigation,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { isAdmin, isSystemAdmin } from '../utils/roleUtils';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedButton, ThemedCard, ThemedInput } from './themed';
import DynamicPreferenceField from './preferences/DynamicPreferenceField';
import { getCategoryDisplayName, getCategoryIcon } from '../utils/preferenceIcons';
import { usePreferences } from '../contexts/PreferenceContext';
import { getImageUrl } from '../utils/imageUrl';

const OrganizationSettings = ({ currentEntity, userRole }) => {
  const { t } = useTranslation();
  const { text, cn } = useTheme();
  const { formatDateISO } = useDateTimePreferences();
  const { refreshEntitySettings } = usePreferences();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Reference data from database
  const [countries, setCountries] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [loadingReferenceData, setLoadingReferenceData] = useState(true);
  
  // Dynamic preference definitions and settings
  const [preferenceDefinitions, setPreferenceDefinitions] = useState({});
  const [availableCategories, setAvailableCategories] = useState([]);
  const [dynamicSettings, setDynamicSettings] = useState({});
  const [entityPreferenceKeys, setEntityPreferenceKeys] = useState([]);
  const [fetchedEntityData, setFetchedEntityData] = useState(null);
  const [preferenceMetadata, setPreferenceMetadata] = useState({}); // NEW: Store preference record IDs for translations
  const hasInitializedDynamicSettings = useRef(false);
  
  const [entitySettings, setEntitySettings] = useState({
    name: '',
    entity_type: '',
    status: 'active',
    expiry_date: '',
    logo_url: '',
    time_zone: 'America/Toronto',
    address: '',
    city: '',
    province: '',
    postcode: '',
    country: '',
    latitude: '',
    longitude: ''
  });

  const [bookingSettings, setBookingSettings] = useState({
    booking_window_days: 30,
    allow_recurring_bookings: true,
    booking_required_confirmation: false,
    eta_source: 'google'
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Change tracking for organization settings
  const entityTracker = useChangeTracker({});
  const bookingTracker = useChangeTracker({});
  const dynamicTracker = useChangeTracker({});
  const {
    tabsRef,
    canScrollLeft,
    canScrollRight,
    checkTabsScroll,
    scrollTabs
  } = useScrollableTabs({ refreshKey: availableCategories.join('|') });

  // Load reference data on component mount
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        setLoadingReferenceData(true);
        const locationData = await referenceService.getLocationData();
        setCountries(locationData.countries);
        setTimezones(locationData.timezones);
      } catch (error) {
        console.error('Error loading reference data:', error);
        toast.error('Failed to load countries and timezones');
      } finally {
        setLoadingReferenceData(false);
      }
    };
    
    loadReferenceData();
  }, []);

  // Load entity preference definitions on component mount
  useEffect(() => {
    if (!currentEntity) {
      return;
    }

    setDynamicSettings({});
    setFetchedEntityData(null);
    hasInitializedDynamicSettings.current = false;
    dynamicTracker.updateOriginalValues({});
    fetchPreferenceDefinitions();
  }, [currentEntity]);

  const fetchPreferenceDefinitions = async () => {
    try {
      console.log(`Fetching entity preference definitions from /api/settings/entity/${currentEntity.entityId}/preferences/definitions`);
      const response = await axios.get(`/api/settings/entity/${currentEntity.entityId}/preferences/definitions`);
      console.log('Entity preference definitions response:', response);
      
      if (response.data.success && response.data.data) {
        console.log('Setting entity preference definitions:', response.data.data);
        setPreferenceDefinitions(response.data.data);
        
        // Set available categories based on what's returned
        const categories = Object.keys(response.data.data).filter(cat => 
          response.data.data[cat].length > 0 
          && cat !== 'notification_system' 
          && cat !== 'survey_system'
          && cat !== 'organization'
        );
        console.log('Available entity categories:', categories);
        setAvailableCategories(categories);

        const definitionKeys = categories.reduce((accumulator, category) => {
          response.data.data[category].forEach(definition => {
            accumulator.push(definition.preference_key);
          });
          return accumulator;
        }, []);

        // Add organization_type and support_email keys even though we filtered out the organization category
        // These will be displayed in the basic information tab
        if (response.data.data.organization) {
          response.data.data.organization.forEach(definition => {
            if (definition.preference_key === 'organization_type' || definition.preference_key === 'support_email') {
              definitionKeys.push(definition.preference_key);
            }
          });
        }

        setEntityPreferenceKeys(definitionKeys);
        hasInitializedDynamicSettings.current = false;
        
        // Set initial active tab to first available category if still on default
        if (categories.length > 0 && (activeTab === 'general' || activeTab === 'basic')) {
          console.log('Setting initial active tab to:', categories[0]);
          setActiveTab('basic');
          // setActiveTab(categories[0]); // Set initial active tab to first available category - booking in this case
        }
      } else {
        console.error('Invalid response structure:', response.data);
        toast.error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error fetching entity preference definitions:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    }
  };


  useEffect(() => {
    if (currentEntity) {
      fetchEntitySettings();
    }
  }, [currentEntity]);

  const fetchEntitySettings = async () => {
    try {
      const response = await axios.get(`/api/settings/entity/${currentEntity.entityId}`);
      if (response.data.success) {
        const data = response.data.data;

        // Extract and store preference metadata for translation support
        if (data.preferenceMetadata) {
          console.log('Preference metadata received:', data.preferenceMetadata);
          setPreferenceMetadata(data.preferenceMetadata);
        }

        const fetchedEntitySettings = {
          name: data.name || '',
          entity_type: data.entity_type || '',
          status: data.status || 'active',
          expiry_date: formatDateISO(data.expiry_date || data.trial_end_date) || '',
          logo_url: data.logo_url || '',
          time_zone: data.time_zone || 'America/Toronto',
          address: data.address || '',
          city: data.city || '',
          province: data.province || '',
          postcode: data.postcode || '',
          country: data.country || '',
          latitude: data.latitude || '',
          longitude: data.longitude || ''
        };
        const fetchedBookingSettings = {
          booking_window_days: data.booking_window_days || 30,
          allow_recurring_bookings: data.allow_recurring_bookings !== false,
          booking_required_confirmation: data.booking_required_confirmation === true,
          eta_source: data.eta_source || 'google'
        };

        setEntitySettings(fetchedEntitySettings);
        setBookingSettings(fetchedBookingSettings);
        hasInitializedDynamicSettings.current = false;
        setFetchedEntityData(data);

        // Update original values for change tracking
        entityTracker.updateOriginalValues(fetchedEntitySettings);
        bookingTracker.updateOriginalValues(fetchedBookingSettings);

        if (data.logo_url) {
          setLogoPreview(getImageUrl(data.logo_url));
        }
      }
    } catch (error) {
      toast.error(t('errorFetchingEntitySettings'));
      console.error('Error fetching entity settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneralSettingChange = (field, value) => {
    setEntitySettings(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const handleDynamicSettingChange = (key, value) => {
    setDynamicSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  useEffect(() => {
    if (!fetchedEntityData || entityPreferenceKeys.length === 0 || hasInitializedDynamicSettings.current) {
      return;
    }

    const initialDynamicSettings = entityPreferenceKeys.reduce((accumulator, key) => {
      if (Object.prototype.hasOwnProperty.call(fetchedEntityData, key)) {
        accumulator[key] = fetchedEntityData[key];
      } else {
        accumulator[key] = '';
      }
      return accumulator;
    }, {});

    console.log('Initialized dynamic settings:', initialDynamicSettings);

    setDynamicSettings(initialDynamicSettings);
    dynamicTracker.updateOriginalValues(initialDynamicSettings);
    hasInitializedDynamicSettings.current = true;
  }, [fetchedEntityData, entityPreferenceKeys]);

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('logoSizeError'));
        return;
      }
      
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('logo', logoFile);

    try {
      const response = await axios.post(
        `/api/settings/entity/${currentEntity.entityId}/logo`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success(t('logoUploadedSuccessfully'));
        setEntitySettings(prev => ({
          ...prev,
          logo_url: response.data.logoUrl
        }));
        setLogoFile(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('errorUploadingLogo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    let logoUploaded = false;
    try {
      // Upload logo first if there's a new one
      if (logoFile) {
        await handleLogoUpload();
        logoUploaded = true;
      }

      // Get only changed fields
      const changedEntitySettings = entityTracker.getChangedFields(entitySettings);
      const changedBookingSettings = bookingTracker.getChangedFields(bookingSettings);
      const changedDynamicSettings = dynamicTracker.getChangedFields(dynamicSettings);

      // Combine only the changed fields
      const changedSettings = {
        ...changedEntitySettings,
        ...changedBookingSettings,
        ...changedDynamicSettings
      };

      // Only make API call if there are changes
      if (Object.keys(changedSettings).length > 0) {
        console.log('Saving changed organization settings:', changedSettings);

        const response = await axios.put(
          `/api/settings/entity/${currentEntity.entityId}`,
          changedSettings
        );

        if (response.data.success) {
          // Update original values after successful save
          entityTracker.updateOriginalValues(entitySettings);
          bookingTracker.updateOriginalValues(bookingSettings);
          dynamicTracker.updateOriginalValues(dynamicSettings);

          // Refresh entity settings in preference context so other components get updated values
          await refreshEntitySettings();

          toast.success(t('entitySettingsUpdatedSuccessfully'));
        }
      } else if (!logoUploaded) {
        // Only show "no changes" if we didn't upload a logo either
        toast.info(t('noChangesToSave'));
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('errorUpdatingEntitySettings'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAdmin(userRole)) {
    return (
      <div className="text-center py-8">
        <p className={cn("text-gray-500 dark:text-gray-400")}>{t('noPermissionToViewSettings')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className={cn("text-2xl font-bold", text.primary)}>{t('organizationSettings')}</h2>
        <p className={cn("mt-1 text-sm", text.muted)}>
          {t('manageOrganizationSettings', { name: currentEntity?.entityName })}
        </p>
      </div>

      {/* Dynamic Tabs */}
      <div className={cn("border-b relative", "border-gray-200 dark:border-gray-700")}>
        {/* Left scroll indicator */}
        {canScrollLeft && (
          <button
            onClick={() => scrollTabs('left')}
            className={cn(
              "absolute left-0 top-0 bottom-0 z-10 px-1 flex items-center",
              "bg-gradient-to-r from-white via-white to-transparent dark:from-gray-900 dark:via-gray-900"
            )}
            aria-label="Scroll tabs left"
          >
            <ChevronLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        )}

        {/* Right scroll indicator */}
        {canScrollRight && (
          <button
            onClick={() => scrollTabs('right')}
            className={cn(
              "absolute right-0 top-0 bottom-0 z-10 px-1 flex items-center",
              "bg-gradient-to-l from-white via-white to-transparent dark:from-gray-900 dark:via-gray-900"
            )}
            aria-label="Scroll tabs right"
          >
            <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        )}

        <nav
          ref={tabsRef}
          onScroll={checkTabsScroll}
          className="-mb-px flex space-x-8 overflow-x-auto scrollbar-thin"
        >          
          {/* Special static tabs for complex fields */}
          {/* Basic Information Tab */}
          <button
            onClick={() => setActiveTab('basic')}
            className={cn(
              "py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0",
              activeTab === 'basic'
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : cn("border-transparent", text.muted, "hover:border-gray-300 dark:hover:border-gray-600", "hover:text-gray-700 dark:hover:text-gray-300")
            )}
          >
            <Building className="inline w-4 h-4 mr-1" />
            {t('basicInformation')}
          </button>
          
          {/* Location & Timezone Tab */}
          <button
            onClick={() => setActiveTab('location')}
            className={cn(
              "py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0",
              activeTab === 'location'
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : cn("border-transparent", text.muted, "hover:border-gray-300 dark:hover:border-gray-600", "hover:text-gray-700 dark:hover:text-gray-300")
            )}
          >
            <Clock className="inline w-4 h-4 mr-1" />
            {t('locationTimezone')}
          </button>

          {/* Dynamic tabs based on available categories */}
          {availableCategories.map(category => {
            const CategoryIcon = getCategoryIcon(category);
            return (
              <button
                key={category}
                onClick={() => setActiveTab(category)}
                className={cn(
                  "py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors whitespace-nowrap flex-shrink-0",
                  activeTab === category
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : cn("border-transparent", text.muted, "hover:border-gray-300 dark:hover:border-gray-600", "hover:text-gray-700 dark:hover:text-gray-300")
                )}
              >
                <CategoryIcon className="w-4 h-4 mr-1" />
                {t(getCategoryDisplayName(category))}
              </button>
            );
          })}

        </nav>
      </div>

      {/* Dynamic preference tabs */}
      {availableCategories.map(category => (
        activeTab === category && (
          <ThemedCard key={category}>
            <ThemedCard.Body className="space-y-6">
              {preferenceDefinitions[category] && preferenceDefinitions[category].map(definition => (
                <DynamicPreferenceField
                  key={definition.preference_key}
                  definition={definition}
                  value={dynamicSettings[definition.preference_key]}
                  onChange={handleDynamicSettingChange}
                  disabled={saving}
                  entityId={currentEntity.entityId}
                  preferenceMetadata={preferenceMetadata[definition.preference_key]}
                />
              ))}
            </ThemedCard.Body>
          </ThemedCard>
        )
      ))}

      {/* Basic Information Tab */}
      {activeTab === 'basic' && (
        <ThemedCard>
          <ThemedCard.Body className="space-y-6">
            <ThemedInput
              label={
                <>
                  <Building className="inline w-4 h-4 mr-1" />
                  {t('organizationName')}
                </>
              }
              type="text"
              value={entitySettings.name}
              onChange={(e) => handleGeneralSettingChange('name', e.target.value)}
            />

            {/* Organization Type Field */}
            <div>
              <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
                <Building className="inline w-4 h-4 mr-1" />
                {t('organizationType')}
              </label>
              <select
                value={dynamicSettings.organization_type || ''}
                onChange={(e) => handleDynamicSettingChange('organization_type', e.target.value)}
                className={cn(
                  "w-full px-3 py-2 border rounded-md transition-all duration-200",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700",
                  text.primary,
                  "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                  "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                )}
              >
                <option value="company">{t('company')}</option>
                <option value="school">{t('school')}</option>
                <option value="university">{t('university')}</option>
                <option value="government">{t('government')}</option>
                <option value="nonprofit">{t('nonprofit')}</option>
                <option value="other">{t('other')}</option>
              </select>
              <p className={cn("mt-1 text-xs", text.muted)}>
                {t('typeOfOrganization')}
              </p>
            </div>

            {/* Support Email Field */}
            <ThemedInput
              label={
                <>
                  <Building className="inline w-4 h-4 mr-1" />
                  {t('supportEmail')}
                </>
              }
              type="email"
              value={dynamicSettings.support_email || ''}
              onChange={(e) => handleDynamicSettingChange('support_email', e.target.value)}
              placeholder="support@example.com"
              helperText={t('entitySupportEmailAddress')}
            />

            {/* Entity Status - Only visible to super_admin */}
            {isSystemAdmin(userRole) && (
              <div>
                <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
                  {t('organizationStatus')}
                </label>
                <select
                  value={entitySettings.status}
                  onChange={(e) => handleGeneralSettingChange('status', e.target.value)}
                  className={cn(
                    "w-full px-3 py-2 border rounded-md transition-all duration-200",
                    "border-gray-300 dark:border-gray-600",
                    "bg-white dark:bg-gray-700",
                    text.primary,
                    "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                    "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                  )}
                >
                  <option value="active">{t('statusActive')}</option>
                  <option value="inactive">{t('statusInactive')}</option>
                  <option value="trial">{t('statusTrial')}</option>
                  <option value="suspended">{t('statusSuspended')}</option>
                </select>
                <p className={cn("mt-1 text-xs", text.muted)}>
                  {t('organizationStatusDescription')}
                </p>
              </div>
            )}

            {/* Expiry Date - Visible to admin and above, editable by super_admin and above */}
            {isAdmin(userRole) && (
              <ThemedInput
                label={
                  <>
                    <Calendar className="inline w-4 h-4 mr-1" />
                    {t('expiryDate')}
                  </>
                }
                type="date"
                value={entitySettings.expiry_date}
                onChange={(e) => handleGeneralSettingChange('expiry_date', e.target.value)}
                disabled={!isSystemAdmin(userRole)}
                helperText={t('expiryDateDescription')}
              />
            )}

            <div>
              <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
                <Image className="inline w-4 h-4 mr-1" />
                {t('organizationLogo')}
              </label>
              <div className="flex items-center space-x-4">
                {logoPreview && (
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt={t('organizationLogo')}
                      className={cn("h-20 w-20 object-contain border rounded", "border-gray-200 dark:border-gray-700")}
                      crossOrigin="anonymous"
                    />
                    {logoFile && (
                      <button
                        onClick={() => {
                          setLogoFile(null);
                          setLogoPreview(entitySettings.logo_url ? getImageUrl(entitySettings.logo_url) : null);
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={handleLogoSelect}
                    className="hidden"
                  />
                  <label
                    htmlFor="logo-upload"
                    className={cn(
                      "cursor-pointer inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md transition-colors",
                      "border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700",
                      text.secondary,
                      "hover:bg-gray-50 dark:hover:bg-gray-600"
                    )}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {logoPreview ? t('changeLogo') : t('uploadLogo')}
                  </label>
                  <p className={cn("mt-1 text-xs", text.muted)}>{t('logoRequirements')}</p>
                </div>
              </div>
            </div>
          </ThemedCard.Body>
        </ThemedCard>
      )}

      {/* Location & Timezone Tab */}
      {activeTab === 'location' && (
        <ThemedCard>
          <ThemedCard.Body className="space-y-6">
            <div>
              <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
                <Clock className="inline w-4 h-4 mr-1" />
                {t('timezone')}
              </label>
              <select
                value={entitySettings.time_zone}
                onChange={(e) => handleGeneralSettingChange('time_zone', e.target.value)}
                className={cn(
                  "w-full px-3 py-2 border rounded-md transition-all duration-200",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700",
                  text.primary,
                  "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                  "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600",
                  loadingReferenceData && "opacity-50 cursor-not-allowed"
                )}
                disabled={loadingReferenceData}
              >
              {loadingReferenceData ? (
                <option value="">Loading timezones...</option>
              ) : (
                timezones.map(tz => (
                  <option key={tz.iana_name} value={tz.iana_name}>
                    {t(tz.display_name)} ({tz.utc_offset})
                  </option>
                ))
              )}
            </select>
          </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ThemedInput
                label={
                  <>
                    <MapPin className="inline w-4 h-4 mr-1" />
                    {t('address')}
                  </>
                }
                type="text"
                value={entitySettings.address}
                onChange={(e) => handleGeneralSettingChange('address', e.target.value)}
              />

              <ThemedInput
                label={t('city')}
                type="text"
                value={entitySettings.city}
                onChange={(e) => handleGeneralSettingChange('city', e.target.value)}
              />

              <ThemedInput
                label={t('stateProvince')}
                type="text"
                value={entitySettings.province}
                onChange={(e) => handleGeneralSettingChange('province', e.target.value)}
              />

              <ThemedInput
                label={t('postalCode')}
                type="text"
                value={entitySettings.postcode}
                onChange={(e) => handleGeneralSettingChange('postcode', e.target.value)}
              />

              <div>
                <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
                  {t('country')}
                </label>
                <select
                  value={entitySettings.country}
                  onChange={(e) => handleGeneralSettingChange('country', e.target.value)}
                  className={cn(
                    "w-full px-3 py-2 border rounded-md transition-all duration-200",
                    "border-gray-300 dark:border-gray-600",
                    "bg-white dark:bg-gray-700",
                    text.primary,
                    "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                    "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600",
                    loadingReferenceData && "opacity-50 cursor-not-allowed"
                  )}
                  disabled={loadingReferenceData}
                >
                  {loadingReferenceData ? (
                    <option value="">Loading countries...</option>
                  ) : (
                    countries.map(country => (
                      <option key={country.iso_code} value={country.iso_code}>
                        {country.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ThemedInput
                label={
                  <>
                    <Navigation className="inline w-4 h-4 mr-1" />
                    {t('latitude')}
                  </>
                }
                type="number"
                step="0.000001"
                value={entitySettings.latitude}
                onChange={(e) => handleGeneralSettingChange('latitude', e.target.value)}
              />

              <ThemedInput
                label={
                  <>
                    <Navigation className="inline w-4 h-4 mr-1" />
                    {t('longitude')}
                  </>
                }
                type="number"
                step="0.000001"
                value={entitySettings.longitude}
                onChange={(e) => handleGeneralSettingChange('longitude', e.target.value)}
              />
            </div>
          
            <div className={cn(
              "p-4 rounded-md border",
              "bg-blue-50 dark:bg-blue-900/20",
              "border-blue-200 dark:border-blue-800"
            )}>
              <div className="flex">
                <Navigation className={cn("w-5 h-5 mt-0.5", "text-blue-400 dark:text-blue-300")} />
                <div className="ml-3">
                  <h3 className={cn("text-sm font-medium", "text-blue-800 dark:text-blue-200")}>
                    {t('coordinatesRecommendation')}
                  </h3>
                  <div className={cn("mt-2 text-sm", "text-blue-700 dark:text-blue-300")}>
                    <p>{t('coordinatesRecommendationDescription')}</p>
                  </div>
                </div>
              </div>
            </div>
          </ThemedCard.Body>
        </ThemedCard>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <ThemedButton
          onClick={handleSaveSettings}
          disabled={saving || uploadingLogo}
          loading={saving}
          icon={Save}
        >
          {t('saveSettings')}
        </ThemedButton>
      </div>
    </div>
  );
};

export default OrganizationSettings;
